import { trpc } from "@/lib/trpc";
import { threadValidators } from "@shared/validators/thread-validators";
import { useRef, useEffect } from "react";
import { z } from "zod";

interface UseInfiniteScrollOptions {
    /** IntersectionObserver options */
    observer?: IntersectionObserverInit;
    /** Enable or disable the query */
    enabled?: boolean;
    filter?: z.infer<typeof threadValidators.threadFilterSchema>;
    limit?: number;
}

export const useThreadsQuery = (filter?: z.infer<typeof threadValidators.threadFilterSchema>, limit?: number, enabled?: boolean) => {
    return trpc.threads.findMany.useInfiniteQuery(
        {
            filter,
            limit
        },
        {
            enabled,
            getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
        }
    );
};
export function useInfiniteScrollThreads(
    { enabled = true, observer, filter, limit }: UseInfiniteScrollOptions = {}
) {
    const query = useThreadsQuery(filter, limit, enabled);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    // Whenever “bottomRef” intersects, fetch the next page if available
    useEffect(() => {
        if (!bottomRef.current) return;

        const obs = new IntersectionObserver((entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && query.hasNextPage) {
                query.fetchNextPage();
            }
        }, observer);

        obs.observe(bottomRef.current);
        return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bottomRef, query.hasNextPage, query.fetchNextPage, observer, filter]);
    const items = query.data?.pages?.flatMap((page) => page.data) ?? [];
    return {
        ...query,
        items,
        bottomRef,
    };
}